import { useEffect, useState } from "react";
import { Ban, CheckCircle2, Eye, EyeOff, Mail, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Empty, Field, NSelect } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtShort, timeAgo } from "@/lib/fleet";

const Th = ({ children }) => <th className="gt-eyebrow whitespace-nowrap px-4 py-3 text-left">{children}</th>;
const Td = ({ children, mono }) => <td className={`whitespace-nowrap px-4 py-3 ${mono ? "font-mono text-xs" : ""}`}>{children}</td>;
export const TableCard = ({ children, testId }) => (
  <div className="gt-card overflow-hidden" data-testid={testId}><div className="gt-scroll overflow-x-auto"><table className="w-full text-sm">{children}</table></div></div>
);

function useForm(init) {
  const [f, setF] = useState(init);
  const inp = (k, ph, type = "text", prefix = "form") => (
    <input type={type} value={f[k] ?? ""} onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))} placeholder={ph} className="gt-input" data-testid={`${prefix}-${k}`} />
  );
  return [f, setF, inp];
}

export function CustomersTab() {
  const { isAdmin, isSeller } = useAuth();
  const [items, setItems] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [f, setF, inp] = useForm({});
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const load = () => api.get("/admin/customers").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    if (isAdmin) {
      api.get("/admin/users")
        .then((r) => setSellers(r.data.filter((u) => u.role === "seller" && u.active)))
        .catch((e) => toast.error(errMsg(e)));
    } else {
      setSellers([]);
    }
  }, [isAdmin]);
  const assignSeller = async (customer, sellerId) => {
    try {
      await api.patch(`/admin/customers/${customer.id}/seller`, { seller_id: sellerId || null });
      toast.success("Admin penanggung jawab diperbarui");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const open = (c) => {
    setF(c ? { ...c } : { name: "", company: "", phone: "", contact: "", email: "", password: "", confirm_password: "", address: "", seller_id: "" });
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setDlg(c ? "edit" : "new");
  };
  const save = async () => {
    try {
      if (dlg === "new") {
        if ((f.password || "").length < 8) {
          toast.error("Password minimal 8 karakter");
          return;
        }
        if (f.password !== f.confirm_password) {
          toast.error("Konfirmasi password tidak sama");
          return;
        }
      }
      const body = Object.fromEntries(Object.entries(f).filter(([k, v]) => k !== "confirm_password" && v !== "" && v != null));
      if (dlg === "edit") await api.put(`/admin/customers/${f.id}`, { name: f.name, company: f.company, phone: f.phone, contact: f.contact, address: f.address });
      else await api.post("/admin/customers", body);
      toast.success("Customer disimpan");
      setDlg(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (c) => { await api.patch(`/admin/customers/${c.id}/status`, { active: !c.active }); toast.success(c.active ? "Customer dinonaktifkan" : c.self_registered ? "Pendaftaran disetujui" : "Customer diaktifkan"); load(); };
  return (
    <>
      <div className="mb-3 flex justify-end"><button onClick={() => open(null)} className="flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground" data-testid="customer-add-btn"><Plus className="h-4 w-4" /> Customer baru</button></div>
      <TableCard testId="customers-table">
        <thead className="border-b border-border"><tr><Th>Customer</Th><Th>Kontak</Th><Th>Kendaraan</Th><Th>Akun</Th><Th>Admin penanggung jawab</Th><Th>Status</Th><Th /></tr></thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} className="border-b border-border/50" data-testid={`customer-row-${c.id}`}>
              <Td><div className="font-semibold">{c.name}</div><div className="text-xs text-muted-foreground">{c.company}</div></Td>
              <Td><div>{c.contact || "-"}</div><div className="text-xs text-muted-foreground">{c.email} · {c.phone}</div></Td>
              <Td mono>{c.vehicle_count}</Td><Td mono>{c.user_count}</Td>
              <Td>
                {isAdmin ? (
                  <NSelect value={c.seller_id || ""} onChange={(v) => assignSeller(c, v)} testId={`customer-seller-${c.id}`}>
                    <option value="">Belum ditugaskan</option>
                    {sellers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </NSelect>
                ) : (
                  <span className="text-sm">{c.seller_name || user?.name || "-"}</span>
                )}
              </Td>
              <Td>{!c.active && c.self_registered ? <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-bold text-amber-500" data-testid={`customer-pending-${c.id}`}>Menunggu persetujuan</span> : <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${c.active ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-500"}`}>{c.active ? "Aktif" : "Nonaktif"}</span>}</Td>
              <Td>
                <div className="flex justify-end gap-1">
                  <button onClick={() => open(c)} className="rounded-lg p-1.5 hover:bg-accent" data-testid={`customer-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => toggle(c)} className={`rounded-lg p-1.5 hover:bg-accent ${c.active ? "text-red-500" : "text-emerald-500"}`} data-testid={`customer-toggle-${c.id}`}>{c.active ? <Ban className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}</button>
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </TableCard>
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="border-border bg-popover" data-testid="customer-dialog">
          <DialogHeader><DialogTitle>{dlg === "edit" ? "Edit customer" : "Customer baru"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nama customer" className="col-span-2">{inp("name", "PT Contoh Logistik", "text", "customer")}</Field>
            <Field label="Perusahaan">{inp("company", "PT ...", "text", "customer")}</Field>
            <Field label="Telepon">{inp("phone", "021...", "text", "customer")}</Field>
            <Field label="Nama kontak">{inp("contact", "Nama PIC", "text", "customer")}</Field>
            <Field label="Alamat">{inp("address", "Kota", "text", "customer")}</Field>
            {dlg === "new" && <>
              <Field label="Email login">{inp("email", "pic@perusahaan.com", "email", "customer")}</Field>
              <Field label="Password (min 8)">
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={f.password || ""}
                    onChange={(e) => setF({ ...f, password: e.target.value })}
                    placeholder="Minimal 8 karakter"
                    minLength={8}
                    className="gt-input pr-11"
                    autoComplete="new-password"
                    data-testid="customer-password"
                  />
                  <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-label={showNewPassword ? "Sembunyikan password" : "Tampilkan password"}>
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </Field>
              <Field label="Konfirmasi password">
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={f.confirm_password || ""}
                    onChange={(e) => setF({ ...f, confirm_password: e.target.value })}
                    placeholder="Ulangi password"
                    minLength={8}
                    className="gt-input pr-11"
                    autoComplete="new-password"
                    data-testid="customer-confirm-password"
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-label={showConfirmPassword ? "Sembunyikan password" : "Tampilkan password"}>
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </Field>
              {isAdmin && (
                <Field label="Admin anak penanggung jawab" className="col-span-2">
                  <NSelect value={f.seller_id || ""} onChange={(v) => setF({ ...f, seller_id: v })} testId="customer-new-seller">
                    <option value="">Belum ditugaskan</option>
                    {sellers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </NSelect>
                </Field>
              )}
            </>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Batal</Button><Button onClick={save} data-testid="customer-save">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function FleetAdminTab() {
  const [items, setItems] = useState([]);
  const [sub, setSub] = useState(null);
  const load = () => api.get("/vehicles").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const saveSub = async () => {
    try { await api.put(`/admin/vehicles/${sub.id}/subscription`, { plan: sub.plan || "Pro Tahunan", expires_at: sub.expires_at }); toast.success("Masa aktif diperbarui"); setSub(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const today = new Date().toISOString().slice(0, 10);
  return (
    <>
      <TableCard testId="admin-vehicles-table">
        <thead className="border-b border-border"><tr><Th>Kendaraan</Th><Th>Customer</Th><Th>IMEI</Th><Th>Protokol</Th><Th>Status GPS</Th><Th>Masa aktif</Th><Th /></tr></thead>
        <tbody>
          {items.map((v) => (
            <tr key={v.id} className="border-b border-border/50">
              <Td><div className="font-semibold">{v.name}</div><div className="font-mono text-xs text-muted-foreground">{v.plate}</div></Td>
              <Td>{v.customer_name}</Td><Td mono>{v.imei}</Td><Td mono>{v.device_protocol}</Td>
              <Td><span className={v.status === "offline" ? "text-slate-400" : "text-emerald-500"}>{v.status}</span> <span className="text-xs text-muted-foreground">· {timeAgo(v.server_time)}</span></Td>
              <Td mono><span className={v.expires_at < today ? "text-red-500" : ""}>{v.expires_at}</span></Td>
              <Td><button onClick={() => setSub({ id: v.id, expires_at: v.expires_at, plan: "Pro Tahunan", name: v.name })} className="rounded-lg border border-border px-2 py-1 text-xs font-semibold hover:border-primary" data-testid={`subscription-edit-${v.id}`}>Atur masa aktif</button></Td>
            </tr>
          ))}
        </tbody>
      </TableCard>
      <Dialog open={!!sub} onOpenChange={(o) => !o && setSub(null)}>
        <DialogContent className="border-border bg-popover" data-testid="subscription-dialog">
          <DialogHeader><DialogTitle>Masa aktif · {sub?.name}</DialogTitle></DialogHeader>
          <Field label="Paket"><input className="gt-input" value={sub?.plan || ""} onChange={(e) => setSub({ ...sub, plan: e.target.value })} data-testid="subscription-plan" /></Field>
          <Field label="Aktif sampai"><input type="date" className="gt-input" value={sub?.expires_at || ""} onChange={(e) => setSub({ ...sub, expires_at: e.target.value })} data-testid="subscription-date" /></Field>
          <p className="text-xs text-muted-foreground">Data GPS dari kendaraan yang masa aktifnya habis tidak akan diproses.</p>
          <DialogFooter><Button variant="outline" onClick={() => setSub(null)}>Batal</Button><Button onClick={saveSub} data-testid="subscription-save">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function DevicesTab() {
  const [items, setItems] = useState([]);
  const [unknown, setUnknown] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [f, setF, inp] = useForm({ imei: "", protocol: "gt06", model: "", sim_number: "", supports_cutoff: false });
  const load = () => Promise.all([api.get("/admin/devices"), api.get("/admin/unknown-devices")]).then(([a, b]) => { setItems(a.data); setUnknown(b.data); });
  useEffect(() => { load(); api.get("/admin/protocols").then((r) => setProtocols(r.data)); }, []);
  const save = async () => {
    try { await api.post("/admin/devices", f); toast.success("IMEI terdaftar"); setDlg(false); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (imei) => { try { await api.delete(`/admin/devices/${imei}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <>
      <div className="mb-3 flex justify-end"><button onClick={() => { setF({ imei: "", protocol: "gt06", model: "", sim_number: "", supports_cutoff: false }); setDlg(true); }} className="flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground" data-testid="device-add-btn"><Plus className="h-4 w-4" /> Daftarkan IMEI</button></div>
      {unknown.length > 0 && (
        <div className="gt-card mb-4 border-amber-500/40 p-4" data-testid="unknown-devices">
          <div className="mb-2 text-sm font-bold text-amber-500">Perangkat tidak dikenal mencoba terhubung</div>
          {unknown.map((u) => <div key={u.imei} className="font-mono text-xs text-muted-foreground">{u.imei} · {u.protocol} · {u.count}x · {timeAgo(u.last_seen)}</div>)}
        </div>
      )}
      <TableCard testId="devices-table">
        <thead className="border-b border-border"><tr><Th>IMEI</Th><Th>Protokol</Th><Th>Model</Th><Th>SIM</Th><Th>Cut-off</Th><Th>Kendaraan</Th><Th>Terakhir</Th><Th /></tr></thead>
        <tbody>
          {items.map((d) => (
            <tr key={d.imei} className="border-b border-border/50">
              <Td mono>{d.imei}</Td><Td mono>{d.protocol}</Td><Td>{d.model || "-"}</Td><Td mono>{d.sim_number || "-"}</Td>
              <Td>{d.supports_cutoff ? "Ya" : "Tidak"}</Td>
              <Td>{d.vehicle ? <>{d.vehicle.name} <span className="font-mono text-xs text-muted-foreground">{d.vehicle.plate}</span></> : <span className="text-amber-500">Belum terpasang</span>}</Td>
              <Td>{timeAgo(d.last_seen)}</Td>
              <Td>{!d.vehicle && <button onClick={() => del(d.imei)} className="rounded-lg p-1.5 text-red-500 hover:bg-red-500/10" data-testid={`device-delete-${d.imei}`}><Trash2 className="h-4 w-4" /></button>}</Td>
            </tr>
          ))}
        </tbody>
      </TableCard>
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="border-border bg-popover" data-testid="device-dialog">
          <DialogHeader><DialogTitle>Daftarkan IMEI GPS Tracker</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="IMEI" className="col-span-2">{inp("imei", "15 digit", "text", "device")}</Field>
            <Field label="Protokol"><NSelect value={f.protocol} onChange={(v) => setF({ ...f, protocol: v })} testId="device-protocol">{protocols.map((p) => <option key={p.name} value={p.name}>{p.title}</option>)}</NSelect></Field>
            <Field label="Model">{inp("model", "Zacro CY06", "text", "device")}</Field>
            <Field label="Nomor SIM">{inp("sim_number", "0812...", "text", "device")}</Field>
            <label className="flex items-center gap-2 self-end pb-2 text-sm"><input type="checkbox" checked={f.supports_cutoff} onChange={(e) => setF({ ...f, supports_cutoff: e.target.checked })} className="accent-yellow-500" data-testid="device-cutoff" /> Relay cut-off</label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(false)}>Batal</Button><Button onClick={save} data-testid="device-save">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function AccountsTab() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [f, setF, inp] = useForm({});
  const load = () => api.get("/admin/users").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/admin/customers").then((r) => setCustomers(r.data));
    api.get("/admin/users").then((r) => setSellers(r.data.filter((u) => u.role === "seller" && u.active)));
  }, []);
  const save = async () => {
    try {
      const body = { ...f };
      if (body.role !== "customer") {
        delete body.customer_id;
        delete body.seller_id;
      }
      if (body.role !== "seller") delete body.whatsapp;
      await api.post("/admin/users", body);
      toast.success("Akun dibuat");
      setDlg(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (u) => {
    try { await api.patch(`/admin/users/${u.id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const invite = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/send-invite`);
      toast.success(`Link buat password dikirim ke ${u.email}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const roleLabel = (role) => role === "seller" ? "ADMIN ANAK" : role.toUpperCase();
  return (
    <>
      <div className="mb-3 flex justify-end">
        <button onClick={() => {
          setF({ email: "", password: "", name: "", role: "customer", customer_id: customers[0]?.id, seller_id: "", whatsapp: "" });
          setDlg(true);
        }} className="flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground" data-testid="user-add-btn">
          <Plus className="h-4 w-4" /> Akun baru
        </button>
      </div>
      <TableCard testId="users-table">
        <thead className="border-b border-border">
          <tr><Th>Nama</Th><Th>Email</Th><Th>Peran</Th><Th>Customer</Th><Th>WhatsApp</Th><Th>Login terakhir</Th><Th>Status</Th><Th>Email</Th><Th>Aksi</Th></tr>
        </thead>
        <tbody>
          {items.map((u) => (
            <tr key={u.id} className="border-b border-border/50">
              <Td><span className="font-semibold">{u.name}</span></Td>
              <Td mono>{u.email}</Td>
              <Td><span className={`rounded-md px-1.5 py-0.5 text-[11px] font-bold ${u.role === "admin" ? "bg-primary/15 gt-brand-text" : "bg-accent"}`}>{roleLabel(u.role)}</span></Td>
              <Td>{u.customer_name || "-"}</Td>
              <Td>{u.whatsapp || "-"}</Td>
              <Td>{u.last_login ? fmtShort(u.last_login) : "-"}</Td>
              <Td><button onClick={() => toggle(u)} className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${u.active ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-500"}`} data-testid={`user-toggle-${u.id}`}>{u.active ? "Aktif" : "Nonaktif"}</button></Td>
              <Td>{u.active && <button onClick={() => invite(u)} className="flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] font-bold hover:border-primary/50" data-testid={`user-invite-${u.id}`}><Mail className="h-3 w-3" /> Kirim link</button>}</Td>
              <Td>{u.role !== "admin" && <button onClick={async () => {
                if (!window.confirm(`Hapus akun ${u.email}? Data Customer dan GPS tidak dihapus.`)) return;
                try {
                  await api.delete(`/admin/users/${u.id}`);
                  toast.success("Akun dihapus");
                  load();
                } catch (e) { toast.error(errMsg(e)); }
              }} className="rounded-full border border-red-500/40 px-2 py-0.5 text-xs text-red-500" data-testid={`user-delete-${u.id}`}><Trash2 className="mr-1 inline h-3 w-3" />Hapus</button>}</Td>
            </tr>
          ))}
        </tbody>
      </TableCard>
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="border-border bg-popover" data-testid="user-dialog">
          <DialogHeader><DialogTitle>Akun pengguna baru</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nama">{inp("name", "Nama lengkap", "text", "user")}</Field>
            <Field label="Email">{inp("email", "email@domain.com", "email", "user")}</Field>
            <Field label="Password (min 8)">{inp("password", "••••••••", "password", "user")}</Field>
            <Field label="Peran">
              <NSelect value={f.role || "customer"} onChange={(v) => setF({ ...f, role: v })} testId="user-role">
                <option value="customer">Customer</option>
                <option value="seller">Admin anak</option>
                <option value="admin">Super Admin</option>
              </NSelect>
            </Field>
            {f.role === "seller" && <Field label="Nomor WhatsApp Admin anak" className="col-span-2">{inp("whatsapp", "62812xxxxxxxx", "tel", "user")}</Field>}
            <p className="col-span-2 text-xs text-muted-foreground" data-testid="user-dialog-email-note">Password di atas adalah password sementara yang Anda sampaikan sendiri. Demi keamanan, password tidak dikirim lewat email; pengguna menerima email berisi link untuk membuat password sendiri (berlaku 3 hari).</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(false)}>Batal</Button><Button onClick={save} data-testid="user-save">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function AuditTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/audit-logs").then((r) => setItems(r.data)); }, []);
  return (
    <TableCard testId="audit-table">
      <thead className="border-b border-border"><tr><Th>Waktu</Th><Th>Pengguna</Th><Th>Aksi</Th><Th>Target</Th><Th>IP</Th></tr></thead>
      <tbody>
        {items.map((a) => <tr key={a.id} className="border-b border-border/50"><Td mono>{fmtShort(a.created_at)}</Td><Td>{a.user_email}</Td><Td mono>{a.action}</Td><Td mono>{String(a.target).slice(0, 18)}</Td><Td mono>{a.ip}</Td></tr>)}
      </tbody>
      {!items.length && <tbody><tr><td colSpan={5}><Empty title="Belum ada log" /></td></tr></tbody>}
    </TableCard>
  );
}

export function UsageTab() {
  const [u, setU] = useState(null);
  useEffect(() => { api.get("/admin/usage").then((r) => setU(r.data)); }, []);
  if (!u) return null;
  const items = [["Customer", `${u.active_customers}/${u.customers}`], ["Menunggu persetujuan", u.pending_registrations], ["Ukuran database", u.db_size], ["Kendaraan", u.vehicles], ["Perangkat GPS", u.devices], ["Akun", u.users], ["Total posisi GPS", u.positions_total.toLocaleString("id-ID")], ["Posisi 24 jam", u.positions_24h.toLocaleString("id-ID")], ["Event 24 jam", u.events_24h], ["Critical 24 jam", u.critical_24h], ["Koneksi WebSocket", u.ws_connections], ["IMEI tak dikenal", u.unknown_devices], ["Habis ≤ 30 hari", u.expiring_30d]];
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="usage-grid">
      {items.map(([l, v]) => <div key={l} className="gt-card p-4"><div className="gt-eyebrow">{l}</div><div className="mt-2 font-mono text-2xl font-bold">{v}</div></div>)}
    </div>
  );
}
